/* eslint-disable react-hooks/rules-of-hooks */
import React, { useId } from 'react';
import styled from 'styled-components';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';

import {
  ThemeProvider,
  SYS_BREAKPOINT_SM,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_LG,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_RELATIVE_BP_XS_LG,
  SYS_SPACE_RELATIVE_LG,
} from '@oneaudi/unified-web-common';
import { useContent, handleOpenInLayer } from '@oneaudi/feature-app-utils';

import type { TextButtonProps, TextLinkProps } from '@oneaudi/unified-web-alpha-components';
import { TextButton, TextLink, Badge } from '@oneaudi/unified-web-alpha-components';
import type { TrackingClickEvent } from '../tracking';
import { useTracking } from '../tracking';
import type { ButtonsContent, ButtonElement, OpenIn } from '../../types';
import { getSelectedButtonCombination, isDebugMode, isEmpty } from '../utils';
import { APP_ID } from '../../environment';
import { mapContent } from '../utils/normalize-falcon-data';
import { TrackingContext } from '../context';
import { getThemeName } from '../../alpha-helper';

const alignmentToSlot = (alignment: ButtonsContent['alignment']): 'start' | 'end' =>
  alignment === 'right' ? 'end' : 'start';

const StyledButtonsWrapper = styled.div<{ isTextOnly?: boolean }>`
  margin-block: var(--spacing-relative-xl);
  gap: ${({ isTextOnly }) => (isTextOnly ? SYS_SPACE_RELATIVE_LG : SYS_SPACE_FIXED_150)};
`;

export const StyledContainer = styled.div<{
  paddingTop?: boolean;
  alignment?: ButtonsContent['alignment'];
  isDoubleTextButton?: boolean;
  isTextOnly?: boolean;
  textTwoLayout?: 'horizontal' | 'vertical';
}>`
  transition: 1s all ease;
  transform: scale(1);
  display: flex;
  align-items: center;
  padding-inline: var(--page-margin);

  ${({ alignment }) =>
    !alignment
      ? ''
      : alignment === 'center'
        ? 'justify-content: center;'
        : ` justify-content: flex-${alignmentToSlot(alignment)};`}

  // Force mobile portrait layout on text only to left align
    ${({ isTextOnly }) => (isTextOnly ? 'justify-content: flex-start;' : '')}

  // check again text only screens greater than mobile portrait
  @media screen and (min-width: ${SYS_BREAKPOINT_MD}) {
    ${({ alignment }) =>
      !alignment
        ? ''
        : alignment === 'center'
          ? 'justify-content: center;'
          : ` justify-content: flex-${alignmentToSlot(alignment)};`}
  }

  .buttons {
    display: flex;
    ${({ isTextOnly }) => (!isTextOnly ? 'width 100%;' : '')};
    ${({ paddingTop }) => (!paddingTop ? `margin-block-start: 0;` : '')}
    ${({ alignment }) =>
      !alignment
        ? ''
        : alignment === 'center'
          ? 'justify-content: center;'
          : ` justify-content: flex-${alignmentToSlot(alignment)};`}

@media screen and (min-width: ${SYS_BREAKPOINT_LG}) {
      ${({ isTextOnly }) => (!isTextOnly ? 'width auto;' : '')};
    }
  }

  .buttons {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    ${({ isTextOnly }) => (!isTextOnly ? 'width 100%;' : '')};
    ${({ isTextOnly }) =>
      isTextOnly ? `align-items: flex-start; gap: ${SYS_SPACE_RELATIVE_BP_XS_LG}` : ``};
    ${({ isTextOnly, textTwoLayout, isDoubleTextButton }) =>
      isTextOnly && isDoubleTextButton && textTwoLayout === 'horizontal'
        ? 'flex-direction: row;'
        : `gap: ${isTextOnly ? SYS_SPACE_RELATIVE_BP_XS_LG : SYS_SPACE_FIXED_150}`};
    ${({ isTextOnly }) =>
      isTextOnly ? 'flex-direction: column; justify-content: flex-start;' : ''};
    @media screen and (min-width: ${SYS_BREAKPOINT_SM}) {
      ${({ isTextOnly, textTwoLayout }) =>
        isTextOnly && textTwoLayout === 'horizontal'
          ? 'justify-content: flex-start; flex-direction: row; flex-wrap: wrap'
          : ''};
      ${({ isTextOnly, isDoubleTextButton }) =>
        isTextOnly && !isDoubleTextButton && 'flex-direction: column'};
    }
    // check again text only screens greater than mobile portrait
    @media screen and (min-width: ${SYS_BREAKPOINT_MD}) {
      ${({ isTextOnly }) =>
        !isTextOnly
          ? `flex-direction: row; align-items: flex-start; width: auto; gap: ${SYS_SPACE_FIXED_150}`
          : ''};
      ${({ alignment }) =>
        !alignment
          ? ''
          : alignment === 'center'
            ? 'align-items: center;'
            : ` align-items: flex-${alignmentToSlot(alignment)};`}
    }
  }
`;

const StyledButton = styled(TextButton)<{ buttonLength?: number }>`
  ${({ buttonLength }) => (buttonLength === 1 ? 'margin-bottom: 0;' : '')};
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledLink = styled(TextLink)<{
  isTextOnly?: boolean;
  textTwoLayout?: 'horizontal' | 'vertical';
  isDoubleTextButton?: boolean;
  buttonLength?: number;
}>`
  ${({ buttonLength }) => (buttonLength === 1 ? 'margin-bottom: 0;' : '')};

  &:last-child {
    margin-bottom: 0;
  }
`;
function getPropertyName(i: number): string | undefined {
  let labelName = '';
  if (i === 0) {
    labelName = 'firstLabel';
  }
  if (i === 1) {
    labelName = 'secondLabel';
  }
  if (i === 2) {
    labelName = 'thirdLabel';
  }
  return labelName;
}

export const Buttons = (props: ButtonsContent): React.ReactElement => {
  const ref = React.useRef(null);
  const trackingService = React.useContext(TrackingContext);
  const { sendReadyEvent, registerImpressionTracking, sendClickEvent } =
    useTracking(trackingService);

  if (typeof window !== 'undefined') {
    // skip tracking on the server side where window object is undefined

    React.useEffect(() => {
      sendReadyEvent();
    }, []);

    React.useEffect(() => {
      if (ref?.current) registerImpressionTracking(ref);
    }, [ref]);
  }

  const sendButtonClickEvent = (
    label: string,
    url: string,
    variant: TextButtonProps['variant'] | TextLinkProps['variant'],
    openin: OpenIn,
    position: string,
  ) => {
    const event: TrackingClickEvent = {
      linkLabel: label,
      linkUrl: url,
      elementName: variant,
      headline: '',
      OpenIn: openin,
      position,
    };
    sendClickEvent(event);
  };

  const buttons: ButtonElement = getSelectedButtonCombination(props);

  const isDoubleTextButton =
    buttons?.buttonContent.filter((x) => x?.variant === 'text' || x?.variant === 'standalone')
      ?.length === 2;
  const isTextOnly =
    buttons?.buttonContent.filter((x) => x?.variant === 'text' || x?.variant === 'standalone')
      ?.length > 0;

  const textTwoLayout = buttons?.textTwoLayout || 'horizontal';
  const { themeColor } = props;

  // onesight tracking was removed because it will be discontinued in arcade
  // TODO tracking-service still needs to be implemented instead of onesight tracking

  return (
    <ThemeProvider theme={{ name: getThemeName(themeColor) }}>
      <UeContainer propertyPath="buttons" label="CTAs">
        <StyledContainer
          alignment={props?.alignment}
          isDoubleTextButton={isDoubleTextButton}
          data-testid="buttons"
          ref={ref}
          paddingTop={props?.paddingTop}
          isTextOnly={isTextOnly}
          textTwoLayout={textTwoLayout}
        >
          <StyledButtonsWrapper isTextOnly={isTextOnly} className="buttons" role="group">
            {buttons?.buttonContent.map((x, i) => {
              return x?.label && x.url ? (
                x.label && (x.variant === 'text' || x.variant === 'standalone') ? (
                  <UeReference propertyPath={`buttons.${i}`} label="CTA" key={useId()}>
                    {(ueProps) => (
                      // @ts-expect-error type not undefined
                      <StyledLink
                        key={useId()}
                        variant="standalone"
                        aria-label={x.ariaLabel}
                        onClick={() => {
                          sendButtonClickEvent(
                            x.label,
                            x.url,
                            // @ts-expect-error type not undefined
                            x.variant,
                            x.openIn || 'new',
                            (i + 1).toString(),
                          );
                          if (x.openIn === 'layer') handleOpenInLayer(x?.url, false, true);
                        }}
                        href={x.openIn === 'layer' ? undefined : x.url}
                        isTextOnly={isTextOnly}
                        data-testid="button"
                        size="md"
                        rel="noopener noreferrer"
                        target={x.openIn === 'new' || x.openIn == null ? '_blank' : ''}
                        iconPosition="trailing"
                        icon="forward"
                        textTwoLayout={textTwoLayout}
                        isDoubleTextButton={isDoubleTextButton}
                        buttonLength={buttons.buttonContent.length}
                        {...ueProps}
                      >
                        <UeElement type="text" property={getPropertyName(i)} label="Label">
                          {x.label}
                        </UeElement>
                      </StyledLink>
                    )}
                  </UeReference>
                ) : (
                  <UeReference propertyPath={`buttons.${i}`} label="CTA" key={useId()}>
                    {(ueProps) => (
                      <StyledButton
                        key={useId()}
                        // @ts-expect-error type not undefined
                        variant={x.variant || 'primary'}
                        href={x.openIn === 'layer' ? undefined : x.url}
                        target={x.openIn === 'new' || x.openIn == null ? '_blank' : ''}
                        aria-label={x.ariaLabel}
                        rel="noopener noreferrer"
                        data-testid="button"
                        onClick={() => {
                          sendButtonClickEvent(
                            x.label,
                            x?.url,
                            // @ts-expect-error type not undefined
                            x.variant,
                            x.openIn || 'new',
                            (i + 1).toString(),
                          );
                          if (x.openIn === 'layer') handleOpenInLayer(x?.url, false, true);
                        }}
                        size="sm"
                        buttonLength={buttons.buttonContent.length}
                        {...ueProps}
                      >
                        <UeElement type="text" property={getPropertyName(i)} label="Label">
                          {x.label}
                        </UeElement>
                      </StyledButton>
                    )}
                  </UeReference>
                )
              ) : (
                // use this UI component to warn the author if values in combinations have been missed
                <Badge label={`Button ${x?.variant} has empty label or empty url!`} alert />
              );
            })}
          </StyledButtonsWrapper>
        </StyledContainer>
      </UeContainer>
    </ThemeProvider>
  );
};

export const ButtonsContentLoader = ({
  config: cypressTestData,
  ssrContent,
}: {
  config?: ButtonsContent;
  ssrContent?: ButtonsContent;
}): React.ReactElement | null => {
  const content =
    typeof ssrContent === 'undefined'
      ? cypressTestData && !isEmpty(cypressTestData)
        ? cypressTestData
        : mapContent(useContent<ButtonsContent>())
      : ssrContent;

  if (isDebugMode()) console.debug(`${APP_ID} ->`, { content });

  if (!content) return null;

  return <Buttons {...content} />;
};
